import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import * as Actions from '../redux/actions';
import '../styles/quotes.scss';
import QuoteWizard from './QuoteWizard';

const DRAFTS_PER_PAGE = 10;

const formatDraftWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const draftTitle = (draft) => {
  const name = (draft.customer_name || '').trim();
  if (name) return name;
  const subj = (draft.subject || '').trim();
  if (subj) return subj;
  return `Quote #${draft.id}`;
};

const QuotesMain = (props) => {
  const [draftsPage, setDraftsPage] = useState(1);
  const [listTab, setListTab] = useState('drafts');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    props.showNav(true);
  }, [props.showNav]);

  useEffect(() => {
    setListTab('drafts');
    setDraftsPage(1);
    props.initializeQuotes(props.authToken, {
      draftsPage: 1,
      draftsPerPage: DRAFTS_PER_PAGE,
      draftsList: 'drafts',
    });
  }, [props.authToken]);

  const meta = props.quoteDraftsMeta;
  const totalPages = meta?.total_pages ?? 1;
  const currentPage = meta?.page ?? draftsPage;
  const totalCount = meta?.total_count ?? 0;
  const isSubmittedTab = listTab === 'submitted';

  useEffect(() => {
    if (meta?.page != null && meta.page !== draftsPage) {
      setDraftsPage(meta.page);
    }
  }, [meta?.page]);

  const drafts = props.quoteDrafts || [];

  const fetchListPage = (page, tab = listTab) => {
    const p = Math.max(1, Math.min(totalPages, page));
    setDraftsPage(p);
    props.fetchQuoteDraftsPage(props.authToken, p, DRAFTS_PER_PAGE, tab);
  };

  const switchListTab = (tab) => {
    if (tab === listTab) return;
    setConfirmDeleteId(null);
    setListTab(tab);
    setDraftsPage(1);
    props.fetchQuoteDraftsPage(props.authToken, 1, DRAFTS_PER_PAGE, tab);
  };

  const goToDraftsPage = (nextPage) => {
    const p = Math.max(1, Math.min(totalPages, nextPage));
    if (p === currentPage) return;
    setConfirmDeleteId(null);
    fetchListPage(p, listTab);
  };

  const handleOpenDraft = (id) => {
    setConfirmDeleteId(null);
    props.loadQuoteDraft(props.authToken, id);
  };

  const handleNewQuote = () => {
    setConfirmDeleteId(null);
    if (listTab !== 'drafts') {
      setListTab('drafts');
    }
    setDraftsPage(1);
    props.initializeQuotes(props.authToken, {
      draftsPage: 1,
      draftsPerPage: DRAFTS_PER_PAGE,
      draftsList: 'drafts',
    });
  };

  const handleDeleteDraft = (id) => {
    const wasActive = props.activeQuote?.id === id;
    setConfirmDeleteId(null);
    props.deleteQuote(props.authToken, id, () => {
      if (wasActive) {
        setListTab('drafts');
        setDraftsPage(1);
        props.initializeQuotes(props.authToken, {
          draftsPage: 1,
          draftsPerPage: DRAFTS_PER_PAGE,
          draftsList: 'drafts',
        });
      } else {
        props.fetchQuoteDraftsPage(props.authToken, currentPage, DRAFTS_PER_PAGE, listTab);
      }
    });
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    if (confirmDeleteId === id) {
      handleDeleteDraft(id);
      return;
    }
    setConfirmDeleteId(id);
  };

  const showPagination = totalPages > 1;
  const listNoun = isSubmittedTab ? 'submitted' : 'drafts';

  return (
    <div className="quotes-dashboard">
      <QuoteWizard />
      <section className="quote-drafts-panel" aria-label="Quote drafts and submitted quotes">
        <div className="quote-drafts-toolbar">
          <div className="quote-drafts-tabs" role="tablist" aria-label="Quote list">
            <button
              type="button"
              role="tab"
              aria-selected={!isSubmittedTab}
              className={`quote-drafts-tab${!isSubmittedTab ? ' quote-drafts-tab--active' : ''}`}
              onClick={() => switchListTab('drafts')}
            >
              Drafts
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={isSubmittedTab}
              className={`quote-drafts-tab${isSubmittedTab ? ' quote-drafts-tab--active' : ''}`}
              onClick={() => switchListTab('submitted')}
            >
              Submitted
            </button>
          </div>
          <button type="button" className="quote-drafts-new-link" onClick={handleNewQuote}>
            <AddIcon className="quote-drafts-new-icon" />
            New
          </button>
        </div>
        {drafts.length === 0 ? (
          <p className="quote-drafts-empty">
            {totalCount === 0
              ? isSubmittedTab
                ? 'No submitted quotes yet.'
                : 'No drafts yet — create a new quote to get started.'
              : `No ${listNoun} on this page — use the controls below or create a new quote.`}
          </p>
        ) : (
          <ul className="quote-drafts-list">
            {drafts.map((draft) => {
              const active = props.activeQuote?.id === draft.id;
              const confirmingDelete = confirmDeleteId === draft.id;
              const when = formatDraftWhen(
                isSubmittedTab ? draft.sent_at || draft.updated_at : draft.updated_at
              );
              return (
                <li key={draft.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`quote-draft-row${active ? ' active' : ''}${confirmingDelete ? ' confirm-delete' : ''}`}
                    onClick={() => {
                      if (confirmingDelete) {
                        setConfirmDeleteId(null);
                        return;
                      }
                      handleOpenDraft(draft.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && confirmingDelete) {
                        e.preventDefault();
                        setConfirmDeleteId(null);
                        return;
                      }
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (confirmingDelete) {
                          setConfirmDeleteId(null);
                          return;
                        }
                        handleOpenDraft(draft.id);
                      }
                    }}
                  >
                    <div className="quote-draft-row-main">
                      <span className="quote-draft-title">{draftTitle(draft)}</span>
                      {when ? (
                        <span className="quote-draft-meta">
                          {isSubmittedTab ? 'Sent ' : ''}
                          {when} by {draft.author}
                        </span>
                      ) : null}
                    </div>
                    {!isSubmittedTab ? (
                      <button
                        type="button"
                        className={`quote-draft-delete${confirmingDelete ? ' quote-draft-delete--confirm' : ''}`}
                        title={confirmingDelete ? 'Confirm delete' : 'Delete draft'}
                        aria-label={confirmingDelete ? 'Confirm delete draft' : 'Delete draft'}
                        onClick={(e) => handleDeleteClick(e, draft.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setConfirmDeleteId(null);
                          }
                        }}
                      >
                        {confirmingDelete ? (
                          'Delete?'
                        ) : (
                          <DeleteOutlineIcon className="quote-draft-delete-icon" />
                        )}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {showPagination ? (
          <nav className="quote-drafts-pagination" aria-label={`${listNoun} pages`}>
            <button
              type="button"
              className="quote-drafts-page-btn"
              disabled={currentPage <= 1}
              onClick={() => goToDraftsPage(currentPage - 1)}
            >
              Previous
            </button>
            <span className="quote-drafts-page-info">
              Page {currentPage} of {totalPages}
              {totalCount > 0 ? ` (${totalCount} ${listNoun})` : ''}
            </span>
            <button
              type="button"
              className="quote-drafts-page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => goToDraftsPage(currentPage + 1)}
            >
              Next
            </button>
          </nav>
        ) : null}
      </section>
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    quoteDrafts: state.quoteDrafts,
    quoteDraftsMeta: state.quoteDraftsMeta,
    activeQuote: state.activeQuote,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(QuotesMain);
